/**
 * Sprint Kanban Board Component
 * Drag-and-drop task management using @dnd-kit
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  fetchSprintKanban,
  moveTask,
  updateKanbanBoardOptimistic,
} from '../../store/sprintSlice';
import { SprintTask, KanbanColumn } from '../../entities/sprint/model/types';
import {
  computePositionAtIndex,
  reconcileBoardWithServerTask,
} from '../../utils/kanbanReconcile';
import './SprintKanban.scss';

interface SprintKanbanProps {
  sprintId: string;
}

interface TaskCardProps {
  task: SprintTask;
  onMenuClick?: (event: React.MouseEvent<HTMLElement>, task: SprintTask) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onMenuClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: {
      type: 'task',
      task,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`task-card ${isDragging ? 'dragging' : ''}`}
    >
      <CardContent className="task-card-content">
        <Box className="task-header">
          <Typography variant="body2" className="task-name">
            {task.name}
          </Typography>
          {onMenuClick && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick(e, task);
              }}
              className="task-menu-button"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {task.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            className="task-description"
          >
            {task.description}
          </Typography>
        )}

        <Box className="task-meta">
          {task.priority && (
            <Chip
              label={task.priority}
              size="small"
              color={getPriorityColor(task.priority) as any}
              icon={<FlagIcon />}
              className="priority-chip"
            />
          )}

          {task.storyPoints && task.storyPoints > 0 && (
            <Chip
              label={`${task.storyPoints} pts`}
              size="small"
              variant="outlined"
              className="points-chip"
            />
          )}
        </Box>

        <Box className="task-footer">
          {task.assigneeId && (
            <Tooltip title={task.assigneeName || 'Assigned'}>
              <Avatar className="assignee-avatar" sx={{ width: 24, height: 24 }}>
                {task.assigneeName
                  ? task.assigneeName.charAt(0).toUpperCase()
                  : <PersonIcon fontSize="small" />}
              </Avatar>
            </Tooltip>
          )}

          {task.estimatedHours && task.estimatedHours > 0 && (
            <Box className="time-estimate">
              <AccessTimeIcon fontSize="small" />
              <Typography variant="caption">{task.estimatedHours}h</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

interface KanbanColumnProps {
  column: KanbanColumn;
  tasks: SprintTask[];
  onMenuClick?: (event: React.MouseEvent<HTMLElement>, task: SprintTask) => void;
}

const KanbanColumnComponent: React.FC<KanbanColumnProps> = ({
  column,
  tasks,
  onMenuClick,
}) => {
  const taskIds = tasks.map((task) => task.id);
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      status: column.status,
    },
  });

  return (
    <Box className={`kanban-column ${isOver ? 'column-over' : ''}`}>
      <Box className="column-header">
        <Typography variant="subtitle1" className="column-title">
          {column.name}
        </Typography>
        <Chip
          label={tasks.length}
          size="small"
          className="column-count"
        />
      </Box>

      <Box
        ref={setNodeRef}
        className="column-content"
        sx={{ minHeight: '200px' }}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} onMenuClick={onMenuClick} />
          ))}
        </SortableContext>

        {tasks.length === 0 && (
          <Box className="empty-column">
            <Typography variant="caption" color="text.secondary">
              Drop tasks here
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const SprintKanban: React.FC<SprintKanbanProps> = ({ sprintId }) => {
  const dispatch = useAppDispatch();
  const { kanbanBoard, loading } = useAppSelector((state) => state.sprints);
  const [activeTask, setActiveTask] = useState<SprintTask | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement | null;
    task: SprintTask | null;
  }>({ element: null, task: null });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (sprintId) {
      dispatch(fetchSprintKanban(sprintId));
    }
  }, [sprintId, dispatch]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = kanbanBoard?.columns
      .flatMap((col: any) => col.tasks)
      .find((t: any) => t.id === active.id);

    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveTask(null);

    if (!over || active.id === over.id || !kanbanBoard) {
      return;
    }

    const sourceColumn = kanbanBoard.columns.find((col) =>
      col.tasks.some((t) => t.id === active.id)
    );
    const draggedTask = sourceColumn?.tasks.find((t) => t.id === active.id);
    if (!sourceColumn || !draggedTask) {
      return;
    }

    // Resolve the target column and, if dropped on a task rather than the
    // empty column area, the index to insert at (so within-column reorders
    // and same-column drops land at the right spot instead of the end).
    let targetColumn: KanbanColumn | undefined;
    let overIndex: number | null = null;

    if (over.data.current?.type === 'column') {
      targetColumn = kanbanBoard.columns.find((col) => col.id === over.id);
    } else {
      targetColumn = kanbanBoard.columns.find((col) =>
        col.tasks.some((t) => t.id === over.id)
      );
      if (targetColumn) {
        overIndex = targetColumn.tasks.findIndex((t) => t.id === over.id);
      }
    }

    if (!targetColumn) {
      return;
    }

    const isSameColumn = targetColumn.id === sourceColumn.id;
    const preDragBoard = kanbanBoard;
    const expectedVersion = draggedTask.version ?? 1;

    let newTargetTasks: SprintTask[];
    let movedIndex: number;

    if (isSameColumn) {
      const oldIndex = sourceColumn.tasks.findIndex((t) => t.id === active.id);
      const newIndex = overIndex !== null ? overIndex : sourceColumn.tasks.length - 1;
      newTargetTasks = arrayMove(sourceColumn.tasks, oldIndex, newIndex);
      movedIndex = newIndex;
    } else {
      const insertIndex = overIndex !== null ? overIndex : targetColumn.tasks.length;
      const movedTask = { ...draggedTask, status: targetColumn.status };
      newTargetTasks = [
        ...targetColumn.tasks.slice(0, insertIndex),
        movedTask,
        ...targetColumn.tasks.slice(insertIndex),
      ];
      movedIndex = insertIndex;
    }

    const newPosition = computePositionAtIndex(newTargetTasks, movedIndex);
    newTargetTasks[movedIndex] = { ...newTargetTasks[movedIndex], position: newPosition };

    const updatedColumns = kanbanBoard.columns.map((col) => {
      if (col.id === targetColumn!.id) {
        return { ...col, tasks: newTargetTasks };
      }
      if (col.id === sourceColumn.id) {
        // Already covered above when source === target (same column).
        return { ...col, tasks: col.tasks.filter((t) => t.id !== active.id) };
      }
      return col;
    });

    dispatch(updateKanbanBoardOptimistic({ ...kanbanBoard, columns: updatedColumns }));

    try {
      const result = await dispatch(
        moveTask({
          sprintId,
          taskId: active.id as string,
          expectedVersion,
          status: isSameColumn ? undefined : targetColumn.status,
          position: newPosition,
        })
      ).unwrap();

      // Sync the bumped version (and authoritative status/position) so the
      // next drag on this task sends a fresh expectedVersion.
      dispatch(
        updateKanbanBoardOptimistic(
          reconcileBoardWithServerTask(
            { ...kanbanBoard, columns: updatedColumns },
            result,
          ),
        ),
      );
    } catch (error: any) {
      const conflict = error?.currentTask;
      if (conflict) {
        // Another user moved this task first - reconcile to their state
        // instead of leaving ours stuck or silently overwriting theirs.
        dispatch(updateKanbanBoardOptimistic(reconcileBoardWithServerTask(preDragBoard, conflict)));
      } else {
        // Any other failure (network error, 404, 500): revert to the exact
        // pre-drag snapshot rather than leaving a stale optimistic state.
        dispatch(updateKanbanBoardOptimistic(preDragBoard));
      }
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    task: SprintTask
  ) => {
    setMenuAnchor({ element: event.currentTarget, task });
  };

  const handleMenuClose = () => {
    setMenuAnchor({ element: null, task: null });
  };

  const handleViewDetails = () => {
    // Navigate to task details
    console.log('View details for task:', menuAnchor.task);
    handleMenuClose();
  };

  const handleEditTask = () => {
    // Open edit modal
    console.log('Edit task:', menuAnchor.task);
    handleMenuClose();
  };

  const handleDeleteTask = () => {
    // Delete task
    console.log('Delete task:', menuAnchor.task);
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box className="kanban-loading">
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading kanban board...
        </Typography>
      </Box>
    );
  }

  if (!kanbanBoard || kanbanBoard.columns.length === 0) {
    return (
      <Box className="kanban-empty">
        <Typography variant="h6" color="text.secondary">
          No tasks in this sprint yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add tasks to get started with sprint planning
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="sprint-kanban">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box className="kanban-board">
          {kanbanBoard.columns.map((column: any) => (
            <KanbanColumnComponent
              key={column.id}
              column={column}
              tasks={column.tasks}
              onMenuClick={handleMenuClick}
            />
          ))}
        </Box>

        <DragOverlay>
          {activeTask ? (
            <TaskCard task={activeTask} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Task Context Menu */}
      <Menu
        anchorEl={menuAnchor.element}
        open={Boolean(menuAnchor.element)}
        onClose={handleMenuClose}
        className="task-menu"
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem onClick={handleEditTask}>Edit Task</MenuItem>
        <MenuItem onClick={handleDeleteTask} className="delete-item">
          Delete Task
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SprintKanban;
